import React, { useState, useContext, useEffect } from 'react';
import MatchGroupContext from '../../context/matchGroups/matchGroupContext';
import './InvitationsModal.css';

const InvitationsModal = ({ isOpen, onClose }) => {
  const { getPendingInvitations, respondToInvitation } = useContext(MatchGroupContext);
  
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadInvitations();
    }
  }, [isOpen]);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const result = await getPendingInvitations();
      if (result.success) {
        setInvitations(result.data);
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (groupId, response) => {
    setResponding(groupId);
    try {
      const result = await respondToInvitation(groupId, response);
      if (result.success) {
        // Remove the invitation from the list
        setInvitations(prev => prev.filter(inv => inv._id !== groupId));
        
        // If no more invitations, close modal
        if (invitations.length === 1) {
          setTimeout(() => onClose(), 1000);
        }
      }
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
    } finally {
      setResponding(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invitations-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Group Invitations</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-container">
              <div className="loader-spinner"></div>
              <p>Loading invitations...</p>
            </div>
          ) : invitations.length === 0 ? (
            <div className="no-invitations">
              <i className="fas fa-inbox"></i>
              <h3>No pending invitations</h3>
              <p>You don't have any group invitations at the moment.</p>
            </div>
          ) : (
            <div className="invitations-list">
              {invitations.map(invitation => {
                const sharedInfo = invitation.sharedWith?.find(s => s.status === 'pending');
                return (
                  <div key={invitation._id} className="invitation-item">
                    <div className="invitation-info">
                      <div className="invitation-header">
                        <h3 className="group-name">{invitation.name}</h3>
                        <div className="invitation-meta">
                          <span className="group-type-badge">{invitation.type.toUpperCase()}</span>
                          <span className={`permission-badge ${sharedInfo?.permission || 'view'}`}>
                            {sharedInfo?.permission || 'view'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="invitation-details">
                        <p className="invited-by">
                          <i className="fas fa-user"></i>
                          Invited by: <strong>{invitation.user?.name || invitation.user?.username || 'Unknown'}</strong>
                        </p>
                        
                        {invitation.description && (
                          <p className="group-description">{invitation.description}</p>
                        )}
                        
                        <div className="group-stats">
                          <span className="stat">
                            <i className="fas fa-gamepad"></i>
                            {(invitation.matches?.tcg?.length || 0) + (invitation.matches?.vgc?.length || 0)} matches
                          </span>
                          <span className="stat">
                            <i className="fas fa-calendar"></i>
                            Created {new Date(invitation.date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="invitation-actions">
                      <button
                        className="btn btn-success"
                        onClick={() => handleResponse(invitation._id, 'accepted')}
                        disabled={responding === invitation._id}
                      >
                        {responding === invitation._id ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Accepting...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check"></i>
                            Accept
                          </>
                        )}
                      </button>
                      
                      <button
                        className="btn btn-danger"
                        onClick={() => handleResponse(invitation._id, 'declined')}
                        disabled={responding === invitation._id}
                      >
                        {responding === invitation._id ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Declining...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-times"></i>
                            Decline
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitationsModal;